import debounce from 'lodash.debounce';
import { defaultBps } from '../utils/layout';

export default class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.data = JSON.parse(
      this.querySelector('[data-section-data]')?.textContent || '{}',
    );
    this.headerEl = this.querySelector('[data-section="header"]');
    this.subheaderEl = this.querySelector('[data-subheader]');
    this.body = document.querySelector('body');
    this.subMenus = this.querySelectorAll('[data-submenu]');
    this.overlay = this.querySelector('[data-nav-overlay]');
    this.expandableMenus = this.querySelectorAll('[data-nav-item]');
    this.expandableButtons = this.querySelectorAll('[data-nav-button]');
    this.mobileMenuTrigger = this.querySelector('[data-mobile-menu-trigger]');
    this.closeMenuButtons = this.querySelectorAll('[data-nav-close]');
    this.mobileBackButtons = this.querySelectorAll('[data-nav-back]');
    this.handleHeaderHeight = this.handleHeaderHeight.bind(this);
    this.debouncedHandleHeaderHeight = debounce(this.handleHeaderHeight, 10);
    this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    this.closeMobileMenu = this.closeMobileMenu.bind(this);
    this.closeExpandedNavs = this.closeExpandedNavs.bind(this);
    this.handleCloseMenu = this.handleCloseMenu.bind(this);
  }

  handleHeaderHeight() {
    const headerHeight = this.headerEl.offsetHeight;
    const isScrolled =
      document.body.scrollTop > headerHeight ||
      document.documentElement.scrollTop > headerHeight;
    this.headerEl.classList.toggle('lg:h-20', !isScrolled);
    this.headerEl.classList.toggle('lg:h-[50px]', isScrolled);
  }

  registerScrollListener() {
    if (defaultBps.above.lg.matches) {
      if (this.scrollListening) return;
      this.scrollListening = true;
      window.addEventListener('scroll', this.debouncedHandleHeaderHeight);
    } else {
      if (!this.scrollListening) return;
      window.removeEventListener('scroll', this.debouncedHandleHeaderHeight);
      this.scrollListening = false;
    }
  }

  registerMobileMenuListeners() {
    if (defaultBps.below.lg.matches) {
      if (this.mobileListening) return;
      this.mobileListening = true;
      this.style.setProperty(
        '--header-height',
        `${this.subheaderEl.offsetHeight}px`,
      );

      this.mobileMenuTrigger.addEventListener('click', this.toggleMobileMenu);
      this.closeMenuButtons.forEach((button) =>
        button.addEventListener('click', this.closeMobileMenu),
      );
      this.mobileBackButtons.forEach((button) =>
        button.addEventListener('click', this.closeExpandedNavs),
      );
    } else {
      this.closeMobileMenu();
      if (!this.mobileListening) return;
      this.mobileMenuTrigger.removeEventListener(
        'click',
        this.toggleMobileMenu,
      );
      this.closeMenuButtons.forEach((button) =>
        button.removeEventListener('click', this.closeMobileMenu),
      );
      this.mobileBackButtons.forEach((button) =>
        button.removeEventListener('click', this.closeExpandedNavs),
      );
      this.mobileListening = false;
    }
  }

  toggleMobileMenu() {
    this.body.classList.toggle('open-menu');
  }

  closeMobileMenu() {
    this.body.classList.remove('open-menu');
    this.expandableButtons.forEach((button) =>
      button.parentElement.classList.remove('expanded'),
    );
    this.body.classList.remove('open-submenu');
  }

  closeExpandedNavs() {
    this.expandableButtons.forEach((button) =>
      button.parentElement.classList.remove('expanded'),
    );
    this.body.classList.remove('open-submenu');
  }

  handleCloseMenu(e) {
    if (e.keyCode === 27) {
      this.expandableMenus.forEach((menu) => menu.classList.remove('expanded'));
    }
  }

  registerExpandableButtonListeners() {
    const handleExpandableButtons = (button) => {
      this.expandableButtons.forEach((expandableButton) => {
        if (expandableButton === button) {
          expandableButton.parentElement.classList.toggle('expanded');
          if (defaultBps.below.lg.matches) {
            this.body.classList.add('open-submenu');
          }
        } else {
          expandableButton.parentElement.classList.remove('expanded');
        }
      });
    };

    this.expandableButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (defaultBps.below.lg.matches) {
          handleExpandableButtons(button);
        }
      });

      button.addEventListener('keydown', (e) => {
        if (defaultBps.above.lg.matches && e.keyCode === 13) {
          handleExpandableButtons(button);
        }
      });
    });
  }

  registerEscapeKeyHandler() {
    if (defaultBps.above.lg.matches) {
      if (this.escapeListening) return;
      this.escapeListening = true;
      document.addEventListener('keyup', this.handleCloseMenu);
    } else {
      if (!this.escapeListening) return;
      document.removeEventListener('keyup', this.handleCloseMenu);
      this.escapeListening = false;
    }
  }

  registerOverlayListeners() {
    [...this.expandableMenus, ...this.subMenus].forEach((menu) => {
      menu.addEventListener('mouseover', () => {
        this.overlay.classList.remove('hidden');
      });
      menu.addEventListener('mouseleave', () => {
        this.overlay.classList.add('hidden');
      });
    });
  }

  setHeaderGroupHeight() {
    document.documentElement.style.setProperty(
      '--header-group-height',
      `${
        document
          .querySelector('#shopify-section-header')
          .getBoundingClientRect().bottom
      }px`,
    );
  }

  connectedCallback() {
    this.registerScrollListener();
    this.registerMobileMenuListeners();
    this.registerExpandableButtonListeners();
    this.registerEscapeKeyHandler();
    this.registerOverlayListeners();
    this.setHeaderGroupHeight();
    window.addEventListener('bpChange', () => {
      this.registerScrollListener();
      this.registerMobileMenuListeners();
      this.registerEscapeKeyHandler();
      this.setHeaderGroupHeight();
    });
  }

  disconnectedCallback() {
    if (this.scrollListening) {
      window.removeEventListener('scroll', this.debouncedHandleHeaderHeight);
    }
    this.closeMenuButtons.forEach((button) =>
      button.removeEventListener('click', this.closeMobileMenu),
    );
    this.mobileBackButtons.forEach((button) =>
      button.removeEventListener('click', this.closeExpandedNavs),
    );
    document.removeEventListener('keyup', this.handleCloseMenu);
  }
}

export const webcomponent = 'site-header';
